import { useCallback, useEffect } from 'react';
import { uniqBy } from 'lodash-es';

import {
  defaultTeamContactsPageState,
  editTeamContactProgressState,
  teamContactDeletedIdState,
  teamContactsAtom,
  teamIdState,
} from '../../team.state';
import { teamContactsPageState } from '../../team.state';

import { useDeleteTeamContact } from './use-delete-team-contact.mutation';
import { useGetTeamContacts } from './use-get-team-contacts.query';
import type { ContactFormItem } from '@/features/contacts/contacts';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';

type UseTeamContactsProps = {
  keepDataAfterUnmount: boolean;
};

export const useTeamContacts = (props?: UseTeamContactsProps) => {
  const [contacts, setContacts] = useAtom(teamContactsAtom);
  const resetContacts = useResetAtom(teamContactsAtom);
  const teamId = useAtomValue(teamIdState);

  const setDeletedContact = useSetAtom(teamContactDeletedIdState);
  const [{ page, pageSize }, setTeamContactsPageState] = useAtom(
    teamContactsPageState,
  );
  const resetPagination = () =>
    setTeamContactsPageState(defaultTeamContactsPageState);

  const {
    data: teamContacts,
    isLoading,
    refetch: refetchQuery,
  } = useGetTeamContacts(teamId, {
    page,
    items: pageSize,
  });

  const { mutateAsync: deleteTeamContact } = useDeleteTeamContact();

  useEffect(() => {
    if (!teamContacts || isLoading) return;
    setContacts(old =>
      uniqBy([...old, ...teamContacts], contact => contact.id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamContacts, isLoading]);

  useEffect(() => {
    return () => {
      if (props?.keepDataAfterUnmount) return;
      resetContacts();
    };
  }, [props?.keepDataAfterUnmount, resetContacts, teamId]);

  useEffect(() => {
    resetPagination();
  }, [resetPagination]);

  const setEditTeamContactState = useSetAtom(editTeamContactProgressState);

  const reset = useCallback(() => {
    setEditTeamContactState(false);
  }, [setEditTeamContactState]);

  const refetch = async () => {
    resetPagination();
    await refetchQuery();
  };

  const addContact = useCallback(
    (contact: ContactFormItem) => {
      setContacts(contacts => [...contacts, contact]);
    },
    [setContacts],
  );

  const updateContact = useCallback(
    (updatedContact: ContactFormItem) => {
      setContacts(contacts =>
        contacts.map(oldContact => {
          if (oldContact.id !== updatedContact.id) {
            return oldContact;
          }

          return updatedContact;
        }),
      );
    },
    [setContacts],
  );

  const deleteContact = useCallback(
    async (id: string, executeDeleteApi: boolean) => {
      if (executeDeleteApi && teamId)
        await deleteTeamContact({
          teamId,
          teamContactId: id,
        });
      setDeletedContact(id);
      setContacts(contacts =>
        contacts.filter(oldContact => {
          return oldContact.id !== id;
        }),
      );
    },
    [deleteTeamContact, setContacts, setDeletedContact, teamId],
  );

  const hasNextPage = Boolean(teamContacts && teamContacts.length >= pageSize);

  return {
    isLoading,
    contacts,
    hasNextPage,
    reset,
    refetch,
    addContact,
    updateContact,
    deleteContact,
  };
};
